import React, { useMemo } from 'react';
import clsx from 'clsx';
import Highcharts from 'highcharts/highstock';
import HighchartsReact from 'highcharts-react-official';
import 'highcharts/modules/accessibility';
import { fontDisplay, fontText, lineHeightToFontSize } from 'common-react';
import { useTheme, useHighchartsSharedSetup } from 'ui-components';
import { RetirementCalculatorChartPropsModel } from '../../tools/RetirementCalculator.model';
import { formatAmount } from '../../tools/RetirementCalculator.utils';

import useStyles from './RetirementCalculatorChart.style';

const TICK_REGION_HEIGHT = 340;
const LABEL_HEADROOM = 60;
const PLOT_HEIGHT = TICK_REGION_HEIGHT + LABEL_HEADROOM;
const PLOT_MARGIN_TOP = 10;
const PLOT_MARGIN_BOTTOM = 10;
const CHART_HEIGHT = PLOT_HEIGHT + PLOT_MARGIN_TOP + PLOT_MARGIN_BOTTOM;
const AXIS_WIDTH = 63;
const AXIS_GAP = 76;
const NARROW_CHART_WIDTH = 500;
const NARROW_AXIS_GAP = 8;
const DATA_LABEL_OFFSET = 16;
const BAR_BORDER_RADIUS = 8;
const CHART_SLOTS = 3;
const GROUP_PADDING = 0.065;

const createStackLabelFormatter = (
  formatLabel: (barIndex: number, total: number) => string,
) => function(this: Highcharts.StackItemObject) {
  return this.total ? formatLabel(Number(this.x), Number(this.total)) : '';
};

const formatAxisLabel = function(this: Highcharts.AxisLabelsFormatterContextObject) {
  return `${formatAmount(Number(this.value))} PLN`;
};

export const RetirementCalculatorChart: React.FC<RetirementCalculatorChartPropsModel> = ({
  series,
  barCaptions,
  highlightedBar,
  barsCount,
  scaleTicks,
  description,
  className,
}) => {
  const c = useStyles({});
  const theme = useTheme();
  useHighchartsSharedSetup();

  const captionFont = fontText('normal', theme.fontSize.s, lineHeightToFontSize.s);
  const highlightFont = fontDisplay('normal', theme.fontSize.xl, lineHeightToFontSize.xl);
  const highlightedBarColor = useMemo(
    () =>
      highlightedBar === undefined
        ? undefined
        : [...series]
            .reverse()
            .find(entry => typeof entry.values[highlightedBar] === 'number')?.color,
    [highlightedBar, series],
  );

  const options = useMemo<Highcharts.Options>(
    () => ({
      chart: {
        type: 'column',
        height: CHART_HEIGHT,
        backgroundColor: 'transparent',
        marginTop: PLOT_MARGIN_TOP,
        marginBottom: PLOT_MARGIN_BOTTOM,
        marginLeft: 0,
        marginRight: AXIS_GAP + AXIS_WIDTH,
      },
      title: { text: undefined },
      credits: { enabled: false },
      exporting: { enabled: false },
      legend: { enabled: false },
      tooltip: { enabled: false },
      accessibility: {
        description,
        keyboardNavigation: { enabled: false },
      },
      xAxis: {
        categories: Array.from({ length: Math.max(barsCount, CHART_SLOTS) }, () => ''),
        lineWidth: 0,
        tickLength: 0,
        labels: { enabled: false },
      },
      yAxis: {
        opposite: true,
        reversedStacks: false,
        min: 0,
        max: scaleTicks[scaleTicks.length - 1] * (1 + LABEL_HEADROOM / TICK_REGION_HEIGHT),
        tickPositions: scaleTicks,
        endOnTick: false,
        startOnTick: false,
        title: { text: undefined },
        gridLineColor: theme.color.greys[15],
        stackLabels: {
          enabled: true,
          allowOverlap: true,
          align: 'left',
          textAlign: 'left',
          x: 0,
          y: -DATA_LABEL_OFFSET,
          crop: false,
          overflow: 'allow',
          useHTML: true,
          formatter: createStackLabelFormatter((barIndex, total) => {
            const isHighlighted = barIndex === highlightedBar;
            const amountStyle = isHighlighted && highlightedBarColor
              ? `color:${highlightedBarColor};font:${highlightFont}`
              : '';
            const amountStyleAttribute = amountStyle ? ` style="${amountStyle}"` : '';
            const amount =
              `<span${amountStyleAttribute}>`
              + `${formatAmount(total)} PLN</span>`;
            const caption = barCaptions?.[barIndex];

            return caption
              ? amount
                + `<span style="display:block;font:${captionFont}">${caption}</span>`
              : amount;
          }),
          style: {
            color: theme.color.greys[80],
            font: fontDisplay('normal', theme.fontSize.base, lineHeightToFontSize.base),
            textOutline: 'none',
            whiteSpace: 'nowrap',
          },
        },
        labels: {
          formatter: formatAxisLabel,
          align: 'right',
          x: AXIS_GAP + AXIS_WIDTH,
          style: {
            color: theme.color.greys[80],
            font: fontText('normal', theme.fontSize.s, lineHeightToFontSize.s),
          },
        },
      },
      plotOptions: {
        column: {
          stacking: 'normal',
          borderWidth: 0,
          borderRadius: { radius: BAR_BORDER_RADIUS, scope: 'stack', where: 'all' },
          groupPadding: GROUP_PADDING,
          pointPadding: 0,
          states: { hover: { enabled: false }, inactive: { enabled: false } },
        },
      },
      responsive: {
        rules: [
          {
            condition: { maxWidth: NARROW_CHART_WIDTH },
            chartOptions: {
              chart: { marginRight: NARROW_AXIS_GAP + AXIS_WIDTH },
              yAxis: { labels: { x: NARROW_AXIS_GAP + AXIS_WIDTH } },
            },
          },
        ],
      },
      series: series.map(entry => ({
        type: 'column' as const,
        id: entry.key,
        name: entry.name,
        color: entry.color,
        data: Array.from(
          { length: Math.max(barsCount, CHART_SLOTS) },
          (_unused, index) => entry.values[index] ?? null,
        ),
      })),
    }),
    [
      barCaptions,
      barsCount,
      captionFont,
      description,
      highlightFont,
      highlightedBar,
      highlightedBarColor,
      scaleTicks,
      series,
      theme,
    ],
  );

  return (
    <div className={clsx(c.root, className)} data-testid="RetirementCalculatorChart">
      <HighchartsReact highcharts={Highcharts} options={options} />
    </div>
  );
};
